"use client";

import * as React from "react";
import { Heart, Star, Shield, Droplets, Backpack } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 6,
  product_cat: [17]
};
const wp_query = new WP_Query(wp_query_params);
const tripTabs = [{
  value: "beach",
  label: "Beach Escapes"
}, {
  value: "adventure",
  label: "Adventure"
}, {
  value: "luxury",
  label: "Luxury Stays"
}, {
  value: "family",
  label: "Family Fun"
}, {
  value: "cultural",
  label: "Cultural Tours"
}];
const volumes = ["7 Days", "3 Days", "14 Days", "5 Days", "10 Days", "4 Days"];
const carryLoads = ["All Inclusive", "Bed & Breakfast", "Full Board", "Self Catering", "Guided Tour", "Luxury Suite"];
const weights = ["From R899", "From R450", "From R1200", "From R600", "From R950", "From R550"];
const ratings = [4.8, 4.9, 4.7, 4.6, 5.0, 4.8];
const reviewCounts = [128, 96, 214, 67, 189, 152];
function ProductsGrid() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] rounded-lg bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground">
        Unable to load travel packages at the moment.
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-sm text-muted-foreground">
        No travel packages available right now.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const volume = volumes[i % volumes.length];
      const carry = carryLoads[i % carryLoads.length];
      const weight = weights[i % weights.length];
      const rating = ratings[i % ratings.length];
      const reviews = reviewCounts[i % reviewCounts.length];
      return <article data-index={i} key={product.id} className="group flex flex-col">
            <div className="relative aspect-[4/5] overflow-hidden bg-secondary/40 rounded-lg shadow-md">
              <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
            backgroundSize: "32px 32px"
          }} aria-hidden="true" />
              <Link to={product.link} className="absolute inset-0 block">
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
              </Link>

              <Badge variant="secondary" className="absolute top-4 left-4 rounded-lg bg-background/95 text-primary border-border px-3 py-1 font-default text-xs tracking-wider">
                {volume}
              </Badge>

              <Button variant="ghost" size="icon" aria-label="Add to wishlist" className="absolute top-3 right-3 size-9 rounded-lg bg-background/90 hover:bg-background text-primary">
                <Heart className="size-4" />
              </Button>
            </div>

            <div className="mt-5 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <span className="font-default text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">
                  MARGA TRAVEL EXCLUSIVE
                </span>
                <div className="flex items-center gap-1.5">
                  <Star className="size-3.5 fill-secondary text-secondary" />
                  <span className="font-default text-xs text-foreground">
                    {rating.toFixed(1)}
                  </span>
                  <span className="font-default text-xs text-muted-foreground">
                    ({reviews})
                  </span>
                </div>
              </div>

              <Link to={product.link}>
                <h3 className="font-default text-2xl font-semibold text-primary leading-tight tracking-tight hover:text-secondary transition-colors">
                  {product.title}
                </h3>
              </Link>

              <div className="font-default text-[11px] uppercase tracking-wider text-foreground border-t border-border pt-3">
                Package: {carry} <span className="mx-2 opacity-50">•</span>{" "}
                Rating: Top Rated{" "}
                <span className="mx-2 opacity-50">•</span> {weight}
              </div>

              <div className="flex items-center gap-4 text-primary">
                <div className="flex items-center gap-1.5" title="Travel insurance included">
                  <Backpack className="size-4" strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-1.5" title="Best price guarantee">
                  <Shield className="size-4" strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-1.5" title="24/7 Support">
                  <Droplets className="size-4" strokeWidth={1.5} />
                </div>
              </div>

              <div className="flex items-end justify-between gap-4 pt-2">
                <div>
                  <span className="font-default text-2xl text-primary font-bold">
                    ${product.price}
                  </span>
                </div>
                <AddToCartButton productId={product.id} variant="default" size="sm" buttonClassName="bg-secondary text-white rounded-lg" text="Book Now" showQuantity={false} />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Asiadestinationtabs() {
  return <section data-section-id="368" id="asiadestinationtabs" className="bg-background py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 mb-16">
          <div>
            <span className="font-default text-xs uppercase tracking-[0.3em] text-secondary font-bold">
              Explore the World
            </span>
            <h2 className="mt-5 font-default text-5xl md:text-6xl font-bold text-primary leading-[1.05] tracking-tight">
              Holiday Specials
            </h2>
          </div>
          <div className="flex lg:justify-end lg:items-end">
            <p className="max-w-md text-base leading-relaxed text-foreground font-default">
              Discover our hand-picked travel packages designed for every type of explorer. From serene beach getaways to thrilling mountain adventures, find your perfect escape with Marga Travel.
            </p>
          </div>
        </div>

        <div className="mb-12 border-b border-border">
          <Tabs defaultValue="beach" className="w-full">
            <TabsList className="bg-transparent h-auto p-0 flex flex-wrap gap-2 sm:gap-8 justify-start">
              {tripTabs.map((tab, i) => <TabsTrigger data-index={i} key={tab.value} value={tab.value} className="border-0 border-b-2 border-transparent bg-transparent px-1 pb-4 pt-0 font-default text-xs uppercase tracking-[0.2em] text-primary data-[state=active]:bg-transparent data-[state=active]:border-secondary data-[state=active]:text-secondary data-[state=active]:shadow-none transition-colors hover:text-secondary font-semibold">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        product_cat: [17]
      }}>
          <ProductsGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}